/**
 * EDC Exposure Awareness Engine
 * 
 * Shifted from a medical "score calculator" to an exposure awareness tool.
 * Computes qualitative signals, identifies drivers, and surfaces confidence levels.
 */

import hazardRules from '../data/hazardRules';
import { computeFoodImpacts } from './foodScoring';
import { getProfile } from '../utils/storage';

/**
 * Usage frequency multipliers.
 */
export const FREQUENCY_MULTIPLIERS = {
    daily: 1.0,
    weekly: 0.45,
    occasionally: 0.15,
};

export const FREQUENCY_OPTIONS = [
    { value: 'daily', label: 'Regularly' },
    { value: 'weekly', label: 'Sometimes' },
    { value: 'occasionally', label: 'Rarely' },
];

const EVIDENCE_TYPE_MULTIPLIERS = {
    direct: 1.0,
    proxy: 0.45,
};

const CONFIDENCE_MULTIPLIERS = {
    high: 1.0,
    moderate: 0.7,
    low: 0.4,
};

/**
 * Maps an internal raw penalty scalar to a UI-facing signal band.
 */
function mapPenaltyToSignalLevel(penalty) {
    if (penalty < 1) return 'Low Signal';
    if (penalty <= 4) return 'Low Signal';
    if (penalty <= 10) return 'Moderate Signal';
    return 'Elevated Signal';
}

function getOverallConfidence(hasData, matches, foodExplanations) {
    if (!hasData) return 'low';
    if (matches.length === 0 && foodExplanations.length === 0) return 'moderate'; // Confident it's clean, assuming data is complete

    // Average confidence of the hits
    let totalScore = 0;
    let count = 0;
    for (const m of matches) {
        if (m.confidence === 'high') totalScore += 3;
        else if (m.confidence === 'moderate') totalScore += 2;
        else totalScore += 1;
        count++;
    }

    if (count === 0) return 'moderate';

    const avg = totalScore / count;
    if (avg >= 2.5) return 'high';
    if (avg >= 1.5) return 'moderate';
    return 'low';
}

/**
 * Analyze a single product's ingredients and return matched hazards.
 */
export function analyzeProduct(product) {
    const ingredientText = (product.ingredients || '').toLowerCase();
    const matches = [];

    // Tracks matched categories to apply diminishing returns.
    const categoryHits = {};

    for (const rule of hazardRules) {
        let matched = false;
        for (const pattern of rule.matchPatterns) {
            if (pattern.test(ingredientText)) {
                matched = true;
                break;
            }
        }

        if (matched) {
            categoryHits[rule.category] = (categoryHits[rule.category] || 0) + 1;
            const hitNumber = categoryHits[rule.category];

            // Diminishing returns: 1st hit = 100%, 2nd = 60%, 3rd+ = 35%
            let categoryMultiplier = 1.0;
            if (hitNumber === 2) categoryMultiplier = 0.6;
            else if (hitNumber >= 3) categoryMultiplier = 0.35;

            matches.push({
                ruleId: rule.id,
                category: rule.category,
                impacts: { ...rule.impacts },
                confidence: rule.confidence,
                evidenceType: rule.evidenceType || 'direct',
                shortReason: rule.shortReason || 'Contains flagged compound',
                detailReason: rule.detailReason || rule.mechanism || '',
                exposureNote: rule.exposureNote,
                categoryMultiplier
            });
        }
    }

    return matches;
}

/**
 * Compute daily overview from a list of product events.
 */
export function computeDailyOverview(productEvents, profile, dailyLog) {
    let internalChemicalPenalty = 0;
    let internalFoodPenalty = 0;
    
    // Default to 1.0 if not yet set
    const goalMultipliers = profile.goalMultipliers || { metabolic: 1.0, stress: 1.0, reproductive: 1.0, thyroid: 1.0, skin: 1.0 };

    const behaviorMultipliers = { metabolic: 1.0, stress: 1.0, reproductive: 1.0, thyroid: 1.0, skin: 1.0 };
    if (dailyLog) {
        // Poor sleep: +15% penalty to Stress & Metabolic axes.
        if (dailyLog.sleepGood === false) {
            behaviorMultipliers.metabolic += 0.15;
            behaviorMultipliers.stress += 0.15;
        }
        // High stress: +20% penalty to Stress axis, +10% to Reproductive.
        if (dailyLog.stressHigh === true) {
            behaviorMultipliers.stress += 0.20;
            behaviorMultipliers.reproductive += 0.10;
        }
        // Exercise: -15% reduction across all axes.
        if (dailyLog.exercised === true) {
            Object.keys(behaviorMultipliers).forEach(k => behaviorMultipliers[k] -= 0.15);
        }
    }

    const impacts = { metabolic: 0, stress: 0, reproductive: 0, thyroid: 0, skin: 0 };
    const explanations = {
        chemical: [],
        food: [],
    };

    let totalProductsWithData = 0;

    for (const event of productEvents) {
        const product = event.product || event;
        const isLeaveOn = product.isLeaveOn !== undefined ? product.isLeaveOn : event.isLeaveOn;
        const exposureMultiplier = isLeaveOn ? 1.5 : 0.5;

        const freq = event.frequency || 'daily';
        const freqMultiplier = FREQUENCY_MULTIPLIERS[freq] ?? 1.0;

        let vulnMultiplier = 1.0;
        if (profile.pregnant) vulnMultiplier = 1.4;
        else if (profile.thyroidCondition) vulnMultiplier = 1.2;

        const hasEnoughData = Boolean(product.ingredients?.trim()) ||
            Boolean(product.nova_group) ||
            Boolean(product.additives_tags?.length) ||
            Boolean(product.nutriments && Object.keys(product.nutriments).length > 0);

        if (hasEnoughData) totalProductsWithData++;

        // Chemical
        const matches = analyzeProduct(product);
        let productChemicalPenalty = 0;

        for (const match of matches) {
            const confMult = CONFIDENCE_MULTIPLIERS[match.confidence] || 0.7;
            const evidMult = EVIDENCE_TYPE_MULTIPLIERS[match.evidenceType] || 1.0;
            const catMult = match.categoryMultiplier || 1.0;

            for (const [axis, delta] of Object.entries(match.impacts)) {
                if (delta === 0) continue;

                const absDelta = Math.abs(delta);
                let adjustedDelta = absDelta * exposureMultiplier * freqMultiplier * vulnMultiplier * confMult * evidMult * catMult;

                if (goalMultipliers[axis]) {
                    adjustedDelta = adjustedDelta * goalMultipliers[axis];
                }
                
                if (behaviorMultipliers[axis]) {
                    adjustedDelta = adjustedDelta * behaviorMultipliers[axis];
                }

                if (axis !== 'skin') {
                    productChemicalPenalty += adjustedDelta;
                }
                impacts[axis] += adjustedDelta;

                explanations.chemical.push({
                    axis,
                    driver: `${product.name} (${match.shortReason})`,
                    productName: product.name,
                    category: product.category,
                    contribution: adjustedDelta,
                });
            }
        }
        internalChemicalPenalty += productChemicalPenalty;

        // Food-specific impact — only for actual food products
        if (product.category === 'food' && (product.nova_group || product.additives_tags || product.nutriments)) {
            const foodResult = computeFoodImpacts(product);
            let productFoodPenalty = 0;

            for (const [axis, delta] of Object.entries(foodResult.impacts)) {
                if (delta === 0) continue;
                const absDelta = Math.abs(delta);
                let adjustedDelta = absDelta * freqMultiplier;

                if (goalMultipliers[axis]) {
                    adjustedDelta = adjustedDelta * goalMultipliers[axis];
                }
                
                if (behaviorMultipliers[axis]) {
                    adjustedDelta = adjustedDelta * behaviorMultipliers[axis];
                }

                if (axis !== 'skin') {
                    productFoodPenalty += adjustedDelta;
                }
                impacts[axis] += adjustedDelta;
            }

            internalFoodPenalty += productFoodPenalty;

            for (const expl of foodResult.explanations) {
                explanations.food.push({
                    driver: `${product.name} (${expl.reason})`,
                    productName: product.name,
                    category: 'food',
                    contribution: Object.values(expl.impacts).reduce((a, b) => a + Math.abs(b), 0) * freqMultiplier
                });
            }
        }
    }

    const totalInternalPenalty = internalChemicalPenalty + internalFoodPenalty;
    const overallLevel = totalProductsWithData === 0 && productEvents.length > 0 ? 'Unclear' : mapPenaltyToSignalLevel(totalInternalPenalty);

    // Group drivers by product for cleaner display
    const allExpls = [...explanations.chemical, ...explanations.food].sort((a, b) => b.contribution - a.contribution);
    const driverStrings = Array.from(new Set(allExpls.map(e => e.driver))).slice(0, 6);
    // Legacy flat strings for History page backward compat
    const mainDrivers = driverStrings;
    // Structured grouped drivers for Dashboard
    const groupedDrivers = {};
    for (const expl of allExpls) {
        const name = expl.productName || expl.driver.split('(')[0].trim();
        if (!groupedDrivers[name]) groupedDrivers[name] = { name, signals: [], totalContribution: 0 };
        const signal = expl.driver.includes('(') ? expl.driver.split('(').pop().replace(')', '') : expl.driver;
        if (!groupedDrivers[name].signals.includes(signal)) groupedDrivers[name].signals.push(signal);
        groupedDrivers[name].totalContribution += expl.contribution;
    }
    const topDrivers = Object.values(groupedDrivers).sort((a, b) => b.totalContribution - a.totalContribution).slice(0, 4);

    let mostAffectedAxis = null;
    let maxImpact = 0;
    for (const [axis, val] of Object.entries(impacts)) {
        if (val > maxImpact) {
            maxImpact = val;
            mostAffectedAxis = axis;
        }
    }

    return {
        overallLevel,
        mainDrivers,
        topDrivers,
        mostAffectedAxis,
        impacts, // Exposing for relative breakdown, not raw scores
        confidenceSummary: totalProductsWithData === productEvents.length ? 'high' : 'moderate',
        totalInternalPenalty
    };
}

/**
 * Get product assessment object (replaces getProductScore).
 */
export function getProductAssessment(product, frequency = 'daily') {
    const profile = getProfile() || {};
    const goalMultipliers = profile.goalMultipliers || { metabolic: 1.0, stress: 1.0, reproductive: 1.0, thyroid: 1.0, skin: 1.0 };

    const hasEnoughData = Boolean(product.ingredients?.trim()) ||
        Boolean(product.nova_group) ||
        Boolean(product.additives_tags?.length) ||
        Boolean(product.nutriments && Object.keys(product.nutriments).length > 0);

    if (!hasEnoughData) {
        return {
            signalLevel: 'Unclear',
            confidence: 'low',
            chemicalSignals: [],
            foodSignals: [],
            topDrivers: [],
            usageContext: (product.category === 'food' || product.category === 'food-packaging' || product.source === 'off') ? 'food' : 'unknown',
            totalInternalPenalty: 0,
            hasOptions: false,
            frequency,
            notes: ['Not enough ingredient or food data to assess this item reliably.']
        };
    }

    const matches = analyzeProduct(product);
    const freqMult = FREQUENCY_MULTIPLIERS[frequency] ?? 1.0;

    // EDC ingredient-based impact
    let chemicalPenalty = 0;
    const isLeaveOn = product.isLeaveOn;
    const exposureMult = isLeaveOn ? 1.5 : 0.5;

    for (const match of matches) {
        const confMult = CONFIDENCE_MULTIPLIERS[match.confidence] || 0.7;
        const evidMult = EVIDENCE_TYPE_MULTIPLIERS[match.evidenceType] || 1.0;
        const catMult = match.categoryMultiplier || 1.0;

        let matchTotal = 0;
        for (const [axis, delta] of Object.entries(match.impacts)) {
            let adjustedDelta = Math.abs(delta);
            if (goalMultipliers[axis]) {
                adjustedDelta = adjustedDelta * goalMultipliers[axis];
            }
            if (axis !== 'skin') {
                matchTotal += adjustedDelta;
            }
        }

        chemicalPenalty += matchTotal * exposureMult * freqMult * confMult * evidMult * catMult;
    }

    // Food-specific impact (NOVA, additives, nutriments)
    let foodPenalty = 0;
    let foodExplanations = [];
    if (product.category === 'food' && (product.nova_group || product.additives_tags || product.nutriments)) {
        const foodResult = computeFoodImpacts(product);
        for (const [axis, delta] of Object.entries(foodResult.impacts)) {
            let adjustedDelta = Math.abs(delta);
            if (goalMultipliers[axis]) {
                adjustedDelta = adjustedDelta * goalMultipliers[axis];
            }
            if (axis !== 'skin') {
                foodPenalty += adjustedDelta;
            }
        }
        // Apply frequency to food
        foodPenalty = foodPenalty * freqMult;
        foodExplanations = foodResult.explanations;
    }

    const totalInternalPenalty = chemicalPenalty + foodPenalty;
    const signalLevel = mapPenaltyToSignalLevel(totalInternalPenalty);
    const confidence = getOverallConfidence(hasEnoughData, matches, foodExplanations);

    // Pluck top drivers
    const topDrivers = [];
    for (const match of matches.slice(0, 2)) {
        if (!topDrivers.includes(match.shortReason)) {
            topDrivers.push(match.shortReason);
        }
    }
    for (const f of foodExplanations.slice(0, 2)) {
        if (!topDrivers.includes(f.reason)) {
            topDrivers.push(f.reason);
        }
    }

    return {
        signalLevel,
        confidence,
        chemicalSignals: matches,
        foodSignals: foodExplanations,
        topDrivers: topDrivers.slice(0, 3),
        totalInternalPenalty,
        usageContext: (product.category === 'food' || product.category === 'food-packaging' || product.source === 'off') ? 'food' : (isLeaveOn ? 'leave-on' : 'rinse-off'),
        frequency
    };
}
